import { useEffect } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { Nav } from '@/marketing/blocks/nav';
import { Footer } from '@/marketing/blocks/footer';
import { FinalCTA } from '@/marketing/blocks/final-cta';
import { Section } from '@/marketing/blocks/Section';
import { TweaksPanel } from '@/marketing/blocks/tweaks/TweaksPanel';
import { usePersona, usePersonas } from '@/marketing/queries/usePersonas';
import { usePublicTweaks } from '@/marketing/queries/useTweaks';
import { useAuthStore } from '@/platform/stores/useAuthStore';
import { applyTheme } from '@/platform/applyTheme';
import { useCopy } from '@/i18n';
import { personaSlugSchema, type PersonaSlug, type Persona } from '@/shared/marketing-schemas';

type CtaCopy = { primary: string; secondary: string };
const CTA_FALLBACK: CtaCopy = { primary: 'Start free today', secondary: 'See how it works' };

type PersonaCopy = {
  short: string;
  name: string;
  quote: string;
  who: string;
  pain: string;
  outcome: string;
  timeline?: Array<{ time: string; place: string; title: string; flow: string; line: string }>;
};

type PersonaPageCopy = {
  back_to_home?: string;
  scenarios_heading?: string;
  tier_legend?: { free: string; pack: string; subscription: string };
  also_useful?: string;
};

type ScenariosCopy = Record<
  string,
  { title?: string; subtitle?: string; desc?: string; icon?: string }
>;

const TIER_LABELS = {
  free: '✓ Free',
  pack: '◆ Premium pack',
  subscription: '∞ Subscription',
} as const;

export default function PersonaPage() {
  const { slug: rawSlug } = useParams<{ slug: string }>();
  const slugParse = personaSlugSchema.safeParse(rawSlug);

  const { tweaks } = usePublicTweaks();
  useEffect(() => applyTheme(tweaks), [tweaks]);

  if (!slugParse.success) {
    return <Navigate to="/" replace />;
  }

  return <PersonaPageContent slug={slugParse.data} />;
}

function PersonaPageContent({ slug }: { slug: PersonaSlug }) {
  const { tweaks } = usePublicTweaks();
  const { data: persona, isLoading } = usePersona(slug);
  const { data: allPersonas } = usePersonas();
  const isAdmin = useAuthStore((s) => s.user?.role === 'admin');
  const copy = useCopy();
  const ctaCopy = copy<CtaCopy>(`ctas.${tweaks.ctaCopy}`) ?? CTA_FALLBACK;
  const personaCopy = copy<PersonaCopy>(`personas_copy.${slug}`);
  const pageCopy = copy<PersonaPageCopy>('persona_page') ?? {};
  const scenarioCopy = copy<ScenariosCopy>('scenarios');

  if (isLoading) {
    return (
      <>
        <Nav ctaPrimary={ctaCopy.primary} />
        <Section id="persona-loading" bg="paper">
          <p style={{ textAlign: 'center', color: 'var(--muted)' }}>Loading…</p>
        </Section>
        <Footer />
      </>
    );
  }

  if (!persona) {
    return <Navigate to="/" replace />;
  }

  return (
    <>
      <Nav ctaPrimary={ctaCopy.primary} />

      <PersonaHero persona={persona} copy={personaCopy} pageCopy={pageCopy} />

      <Section id="persona-scenarios" bg="paper" route routeSeed={1}>
        <header style={{ marginBottom: 24 }}>
          {pageCopy.scenarios_heading ? (
            <h2 className="h-section">{pageCopy.scenarios_heading}</h2>
          ) : null}
        </header>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: 14,
          }}
        >
          {persona.scenarios.map((s) => {
            const meta = scenarioCopy?.[s.slug];
            const accentVar = `var(--${persona.accent_color})`;
            return (
              <article
                key={s.slug}
                style={{
                  background: 'var(--paper-2)',
                  border: `1px solid ${accentVar}`,
                  borderRadius: 14,
                  padding: 18,
                }}
              >
                <div
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: 10,
                    letterSpacing: '.12em',
                    textTransform: 'uppercase',
                    color: accentVar,
                  }}
                >
                  {TIER_LABELS[s.tier]}
                </div>
                <h3
                  style={{
                    fontFamily: 'var(--font-display)',
                    fontSize: 18,
                    marginTop: 8,
                  }}
                >
                  {meta?.icon ? <span style={{ marginRight: 6 }}>{meta.icon}</span> : null}
                  {meta?.title ?? s.slug.replace(/-/g, ' ')}
                </h3>
                {meta?.subtitle || meta?.desc ? (
                  <p style={{ fontSize: 13, color: 'var(--muted)', marginTop: 8, lineHeight: 1.5 }}>
                    {meta.subtitle ?? meta.desc}
                  </p>
                ) : null}
              </article>
            );
          })}
        </div>

        {pageCopy.tier_legend ? (
          <div
            style={{
              marginTop: 32,
              display: 'flex',
              gap: 16,
              flexWrap: 'wrap',
              fontSize: 12,
              color: 'var(--muted)',
            }}
          >
            <span>
              <strong style={{ color: 'var(--leaf)' }}>{TIER_LABELS.free}</strong> ·{' '}
              {pageCopy.tier_legend.free}
            </span>
            <span>
              <strong style={{ color: 'var(--coral)' }}>{TIER_LABELS.pack}</strong> ·{' '}
              {pageCopy.tier_legend.pack}
            </span>
            <span>
              <strong style={{ color: 'var(--navy)' }}>{TIER_LABELS.subscription}</strong> ·{' '}
              {pageCopy.tier_legend.subscription}
            </span>
          </div>
        ) : null}
      </Section>

      {allPersonas && allPersonas.length > 1 ? (
        <AlsoUsefulStrip
          persona={persona}
          others={allPersonas.filter((p) => p.slug !== slug)}
          heading={pageCopy.also_useful}
          scenarioCopy={scenarioCopy}
        />
      ) : null}

      <FinalCTA ctaPrimary={ctaCopy.primary} ctaSecondary={ctaCopy.secondary} />
      <Footer />
      {isAdmin ? <TweaksPanel /> : null}
    </>
  );
}

function PersonaHero({
  persona,
  copy,
  pageCopy,
}: {
  persona: Persona;
  copy?: PersonaCopy;
  pageCopy: PersonaPageCopy;
}) {
  const c = copy;
  return (
    <Section id="persona-hero" bg="paper-2">
      <a
        href="/"
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 11,
          color: 'var(--muted)',
          textDecoration: 'none',
        }}
      >
        ← {pageCopy.back_to_home ?? 'Back to home'}
      </a>
      <div style={{ marginTop: 16 }}>
        <div
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 11,
            letterSpacing: '.14em',
            textTransform: 'uppercase',
            color: `var(--${persona.accent_color})`,
          }}
        >
          {String(persona.display_order).padStart(2, '0')} · {c?.short ?? persona.slug}
        </div>
        <h1
          className="h-display"
          style={{
            marginTop: 12,
            fontFamily: 'var(--font-display)',
            fontSize: 'clamp(40px, 5vw, 64px)',
            lineHeight: 1.05,
          }}
        >
          {c?.name ?? persona.slug}
        </h1>
        {c?.quote ? (
          <p
            style={{
              marginTop: 16,
              fontFamily: 'var(--font-display)',
              fontSize: 22,
              fontStyle: 'italic',
              maxWidth: '52ch',
            }}
          >
            "{c.quote}"
          </p>
        ) : null}
        {c?.who ? (
          <p style={{ marginTop: 8, fontSize: 13, color: 'var(--muted)' }}>{c.who}</p>
        ) : null}
        <div
          style={{
            marginTop: 24,
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: 20,
          }}
        >
          {c?.pain ? (
            <div>
              <h4
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 10,
                  letterSpacing: '.12em',
                  textTransform: 'uppercase',
                  color: 'var(--muted)',
                  marginBottom: 6,
                }}
              >
                The pain
              </h4>
              <p style={{ fontSize: 14, lineHeight: 1.55 }}>{c.pain}</p>
            </div>
          ) : null}
          {c?.outcome ? (
            <div>
              <h4
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 10,
                  letterSpacing: '.12em',
                  textTransform: 'uppercase',
                  color: 'var(--muted)',
                  marginBottom: 6,
                }}
              >
                Outcome
              </h4>
              <p
                style={{
                  fontSize: 14,
                  lineHeight: 1.55,
                  background: `var(--paper)`,
                  border: `1px solid var(--${persona.accent_color})`,
                  borderRadius: 12,
                  padding: 12,
                }}
              >
                {c.outcome}
              </p>
            </div>
          ) : null}
        </div>
      </div>
    </Section>
  );
}

function AlsoUsefulStrip({
  persona,
  others,
  heading,
  scenarioCopy,
}: {
  persona: Persona;
  others: Persona[];
  heading?: string;
  scenarioCopy?: ScenariosCopy;
}) {
  return (
    <Section id="also-useful" bg="paper" tight>
      <h2
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 11,
          letterSpacing: '.14em',
          textTransform: 'uppercase',
          color: 'var(--muted)',
          marginBottom: 18,
        }}
      >
        {heading ?? 'Also useful'}
      </h2>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 14,
        }}
      >
        {others.map((other) => {
          const top = other.scenarios[0];
          const meta = top ? scenarioCopy?.[top.slug] : undefined;
          return (
            <a
              key={other.slug}
              href={`/personas/${other.slug}`}
              style={{
                background: 'var(--paper-2)',
                border: '1px solid var(--line)',
                borderRadius: 14,
                padding: 16,
                textDecoration: 'none',
                color: 'inherit',
              }}
            >
              <div
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 10,
                  letterSpacing: '.12em',
                  textTransform: 'uppercase',
                  color: `var(--${other.accent_color})`,
                }}
              >
                {other.slug}
              </div>
              {top ? (
                <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 16, marginTop: 6 }}>
                  {meta?.title ?? top.slug.replace(/-/g, ' ')}
                </h3>
              ) : null}
            </a>
          );
        })}
      </div>
      <p style={{ marginTop: 18, fontSize: 12, color: 'var(--muted)' }}>
        Cross-links from {persona.slug} to other personas.
      </p>
    </Section>
  );
}
